import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import Datastore from "../../communication/Datastore";
import Utilities from "../../foundation/Utility";
import ClientSystem from "../../types/core/ClientSystem";

import appIcon from "../../graphics/app.svg";
import store, { IAppState } from "../../state/Store";
import { REHYDRATE } from "redux-persist";
import { initialiseProductSubscriptions } from "./ProductSubscriptions";
import ProductSubscription from "../../types/core/ProductSubscription";
import { initialiseUserRolesDetails } from "./UserRoles";
import UserRole from "../../types/core/UserRole";
import ClientSystemVersion from "../../types/core/ClientSystemVersion";

// Define the type for our state store entry
type ClientSystemState = {
    clientSystems: ClientSystem[];
    liveSystems: ClientSystem[];
    versions: ClientSystemVersion[];
    systemsStatus: string;
    systemsAge?: Date;
    versionsStatus: string;
    versionsAge?: Date;
    liveStatus: string;
    liveAge?: Date;
    error: string;
};

// Define the initial state for our state store entry
const initialState: ClientSystemState = {
    clientSystems: [],
    liveSystems: [],
    versions: [],
    systemsStatus: "idle",
    versionsStatus: "idle",
    liveStatus: "idle",
    error: null,
};

const appSubscriptionLinks = new Map<string, string>([
    ["Daily Organiser", "https://timetabling.com.au/shop"],
    ["Staffing", "https://timetabling.com.au/shop"],
    ["Course Manager", "https://timetabling.com.au/shop"],
    ["Preferences Manager", "https://timetabling.com.au/shop"],
    ["Daily Reports", "https://timetabling.com.au/shop"],
    ["Teacher Portal", "https://timetabling.com.au/shop"],
    ["Student Portal", "https://timetabling.com.au/shop"],
]);

// Create a slice to hold all our reducers
const clientSystemSlice = createSlice({
    name: "clientSystem",
    initialState: initialState,
    reducers: {
        updateClientSystemDetails: (
            state,
            action: PayloadAction<ClientSystem>,
        ) => {
            const entry = state.clientSystems.find(
                item => item.id === action.payload.id,
            );

            const newRecord = new ClientSystem(action.payload);
            if (newRecord.logoFile && newRecord.logoFile.indexOf("base64,") < 0)
                newRecord.logoFile = Utilities.base64ImageToDataUrl(
                    newRecord.logoFile,
                );
            else if (!newRecord.logoFile) newRecord.logoFile = appIcon;

            if (entry)
                state.clientSystems.splice(
                    state.clientSystems.indexOf(entry),
                    1,
                    newRecord,
                );
            else state.clientSystems.push(newRecord);
        },
        deleteAllClientSystemDetails: (
            state,
            action: PayloadAction<ClientSystem[]>,
        ) => {
            action.payload.forEach(system => {
                let entry = state.clientSystems.find(
                    entry => entry.id === system.id,
                );
                if (entry)
                    state.clientSystems.splice(
                        state.clientSystems.indexOf(entry),
                        1,
                    );
            });
        },
    },
    extraReducers(builder) {
        builder
            .addCase(REHYDRATE, (_, rawData: any) => {
                if (rawData?.key !== "tts") return undefined;
                return {
                    clientSystems: (
                        rawData?.payload?.clientSystem?.clientSystems ?? []
                    ).map(item => new ClientSystem(item)),
                    liveSystems: (
                        rawData?.payload?.clientSystem?.liveSystems ?? []
                    ).map(item => new ClientSystem(item)),
                    versions: rawData?.payload?.clientSystem?.versions ?? [],
                    systemsStatus:
                        !rawData?.payload?.clientSystem?.clientSystems ||
                        !rawData?.payload?.clientSystem?.clientSystems.length
                            ? "idle"
                            : "succeeded",
                    systemsAge:
                        rawData?.payload?.clientSystem?.systemsAge?.toDate(),
                    versionsStatus:
                        !rawData?.payload?.clientSystem?.versions ||
                        !rawData?.payload?.clientSystem?.versions.length
                            ? "idle"
                            : "succeeded",
                    versionsAge:
                        rawData?.payload?.clientSystem?.versionsAge?.toDate(),
                    liveStatus:
                        !rawData?.payload?.clientSystem?.liveSystems ||
                        !rawData?.payload?.clientSystem?.liveSystems.length
                            ? "idle"
                            : "succeeded",
                    liveAge: rawData?.payload?.clientSystem?.liveAge?.toDate(),
                    error: null,
                };
            })
            .addCase(fetchClientSystems.pending, (state, action) => {
                state.systemsStatus = "loading";
            })
            .addCase(fetchClientSystems.fulfilled, (state, action) => {
                state.systemsStatus = "succeeded";
                state.systemsAge = new Date();
                // Add any fetched posts to the array
                const temp = [];
                for (const item of action.payload) {
                    item.logoFile = item.logoFile
                        ? Utilities.base64ImageToDataUrl(item.logoFile)
                        : appIcon;
                    temp.push(new ClientSystem(item));
                }
                state.clientSystems = state.clientSystems.concat(temp);
                state.clientSystems.sort((a, b) => a.order - b.order);
            })
            .addCase(fetchClientSystems.rejected, (state, action) => {
                state.systemsStatus = "failed";
                state.error = action.error.message;
            })
            .addCase(fetchClientSystemsLive.pending, (state, action) => {
                state.liveStatus = "loading";
            })
            .addCase(fetchClientSystemsLive.fulfilled, (state, action) => {
                state.liveStatus = "succeeded";
                state.liveAge = new Date();
                const temp = action.payload.systems.map(
                    item => new ClientSystem(item),
                );

                for (const system of temp) {
                    delete system.logoFile;
                    delete system.readAdminPermissions;
                    delete system.readMultiPermissions;
                    delete system.readSinglePermissions;
                    delete system.writeAdminPermissions;
                    delete system.writeMultiPermissions;
                    delete system.writeSinglePermissions;
                    delete system.createdBy;
                    delete system.createdOn;
                    delete system.editedBy;
                    delete system.editedOn;
                    delete system.recycledBy;
                    delete system.recycledOn;
                    delete system.schoolID;
                    delete system.standard;
                    delete system.version;
                    delete system.certificateExpiry;
                    delete system.certificateFingerprint;
                    delete system.enforceStrictPaths;

                    const subscription = action.payload.subscriptions.find(
                        item => item.clientSystemID === system.id,
                    );
                    system.licenceCount = subscription
                        ? subscription.licenceCount || 0
                        : 0;
                    system.licenceCountDisplay =
                        system.licenceCount === 0
                            ? "Unlimited"
                            : String(system.licenceCount);

                    system.roleUsage = action.payload.userRoles.filter(role =>
                        role.apps.find(app => app === system.id),
                    );
                    system.roleAllocations = system.roleUsage.reduce(
                        (prev, crnt) => prev + (crnt.users || []).length,
                        0,
                    );

                    system.rawLink = appSubscriptionLinks.get(system.name);
                    system.link = !!subscription
                        ? ""
                        : `<a target="_blank" href="${system.rawLink}">Request Trial | Purchase Subscription</a>`;

                    system.expiryDate = subscription
                        ? subscription.expiryDate.toDate()
                        : null;
                }

                state.liveSystems = temp;
                state.liveSystems.sort((a, b) => a.order - b.order);
            })
            .addCase(fetchClientSystemsLive.rejected, (state, action) => {
                state.liveStatus = "failed";
                state.error = action.error.message;
            })
            .addCase(fetchClientSystemVersions.pending, (state, action) => {
                state.versionsStatus = "loading";
            })
            .addCase(fetchClientSystemVersions.fulfilled, (state, action) => {
                state.versionsStatus = "succeeded";
                state.versionsAge = new Date();
                state.versions = action.payload;
            })
            .addCase(fetchClientSystemVersions.rejected, (state, action) => {
                state.versionsStatus = "failed";
                state.error = action.error.message;
            });
    },
});

const { updateClientSystemDetails, deleteAllClientSystemDetails } =
    clientSystemSlice.actions;
export const newClientSystem = () => {
    const newSystem = new ClientSystem();
    newSystem.id = 0;
    newSystem.name = "New Application";
    newSystem.description = "New Application Description";
    newSystem.authenticationToken = Utilities.newGuid().toString();
    newSystem.identificationToken = Utilities.newGuid().toString();
    newSystem.logoFile = appIcon;
    return newSystem;
};
// This is a thunk, they are used to load the state
export const fetchClientSystems = createAsyncThunk(
    "clientSystem/fetchClientSystems",
    async () => {
        const response = await Datastore.getType<ClientSystem[]>(
            "api/ClientSystems",
        );
        return response;
    },
    {
        condition: (_, { getState }) => {
            const { clientSystem } = (getState() as IAppState).db;
            return (
                clientSystem.systemsStatus !== "loading" &&
                (!clientSystem.systemsAge ||
                    clientSystem.systemsAge?.addDays(1) < new Date())
            );
        },
    },
);
export const fetchClientSystemsLive = createAsyncThunk(
    "clientSystem/fetchClientSystemsLive",
    async (_, thunkAPI) => {
        const [systems, subscriptions, userRoles] = await Promise.all([
            Datastore.getType<ClientSystem[]>("api/ClientSystems/Live"),
            thunkAPI.dispatch(initialiseProductSubscriptions()),
            thunkAPI.dispatch(initialiseUserRolesDetails()),
        ]);
        return {
            systems: systems,
            subscriptions: subscriptions.payload as ProductSubscription[],
            userRoles: userRoles.payload as UserRole[],
        };
    },
    {
        condition: (_, { getState }) => {
            const { clientSystem } = (getState() as IAppState).db;
            return (
                clientSystem.liveStatus !== "loading" &&
                (!clientSystem.liveAge ||
                    clientSystem.liveAge?.addDays(1) < new Date())
            );
        },
    },
);
export const fetchClientSystemVersions = createAsyncThunk(
    "clientSystem/fetchClientSystemVersions",
    async () => {
        const response = await Datastore.getType<ClientSystemVersion[]>(
            "api/ClientSystems/Versions",
        );
        return response;
    },
    {
        condition: (_, { getState }) => {
            const { clientSystem } = (getState() as IAppState).db;
            return (
                clientSystem.versionsStatus !== "loading" &&
                (!clientSystem.versionsAge ||
                    clientSystem.versionsAge?.addDays(1) < new Date())
            );
        },
    },
);
export const initialiseClientSystems = async (): Promise<void> => {
    await Promise.all([
        store.dispatch(fetchClientSystems()),
        store.dispatch(fetchClientSystemsLive()),
    ]);
};
export const updateClientSystem = createAsyncThunk(
    "clientSystem/updateClientSystem",
    async (clientSystem: ClientSystem, thunkAPI): Promise<ClientSystem> => {
        const saveData = Utilities.clone(clientSystem) as ClientSystem;

        const encoded = saveData.logoFile?.indexOf("base64,");
        if (encoded >= 0)
            saveData.logoFile = saveData.logoFile.substring(encoded + 7);
        if (saveData.logoFile === appIcon) saveData.logoFile = null;

        let response =
            saveData.id > 0
                ? await Datastore.putType<ClientSystem[]>("api/ClientSystems", [
                      saveData,
                  ])
                : await Datastore.postType<ClientSystem[]>(
                      "api/ClientSystems",
                      [saveData],
                  );
        thunkAPI.dispatch(updateClientSystemDetails(response[0]));
        return response[0];
    },
);
export const reissueCertificate = createAsyncThunk(
    "clientSystem/reissueCertificate",
    async (clientSystem: ClientSystem, thunkAPI): Promise<ClientSystem> => {
        let response = await Datastore.getType<ClientSystem>(
            `api/ClientSystems/${clientSystem.id}/ReissueCertificate`,
        );
        thunkAPI.dispatch(updateClientSystemDetails(response));
        return response;
    },
);
export const deleteClientSystems = createAsyncThunk(
    "clientSystem/deleteClientSystems",
    async (clientSystems: ClientSystem[], thunkAPI) => {
        await Datastore.delete(
            "api/ClientSystems/Delete",
            clientSystems.map(item => item.id),
        );
        thunkAPI.dispatch(deleteAllClientSystemDetails(clientSystems));
    },
);

// We have to export the reducer as this is added to the state store
export default clientSystemSlice.reducer;
